import React, { useState, useEffect } from 'react';
import { Plug, Database, FileText, Send, HardDrive, BookOpen, ScrollText, Boxes, CheckCircle2, XCircle, Copy, Check } from 'lucide-react';
import api from '../api';
import { ListSkeleton } from '../components/ui';

const AREA_ICON = {
  Database: Database, PED: FileText, Social: Send, Vault: HardDrive,
  MemoryProtocol: BookOpen, Log: ScrollText, Altro: Boxes,
};
const AREA_ORDER = ['Database', 'PED', 'Social', 'Vault', 'MemoryProtocol', 'Log', 'Altro'];

const quando = (iso) => {
  if (!iso) return '';
  const d = new Date(iso), diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s fa`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
  return d.toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', minWidth: 120 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{sub}</div>}
    </div>
  );
}

export default function StatusMcp() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [aperto, setAperto] = useState(null);   // tool espanso
  const [copiato, setCopiato] = useState(false);

  const load = () => api.get('/mcp/status').then(r => { setData(r.data); setErr(false); }).catch(() => setErr(true));
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);   // live: refresh ogni 5s
    return () => clearInterval(id);
  }, []);

  if (!data && !err) return <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}><ListSkeleton rows={6} /></div>;
  if (err) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--red)' }}>Impossibile caricare lo stato MCP.</div>;

  const { server, tools, attivita } = data;
  const areas = {};
  (tools || []).forEach(t => { (areas[t.area] = areas[t.area] || []).push(t); });

  const copia = () => { navigator.clipboard?.writeText(server.endpoint); setCopiato(true); setTimeout(() => setCopiato(false), 1500); };

  return (
    <div className="eden-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <Plug size={26} color="var(--cyan)" />
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Status MCP</h1>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4, fontSize: 12, color: 'var(--green)' }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--green)', animation: 'eden-pulse 1.6s ease-in-out infinite' }} /> live
        </span>
      </div>
      <p style={{ margin: '0 0 20px 38px', color: 'var(--text-muted)', fontSize: 14 }}>Stato del server MCP EdenFlow, tool esposti e attività degli agenti</p>

      {/* Stato server */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--green-dim)', border: '1px solid var(--green)', borderRadius: 10, padding: '12px 16px' }}>
          <CheckCircle2 size={18} color="var(--green)" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>MCP attivo</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>montato nel backend EdenFlow</div>
          </div>
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Endpoint</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{ fontSize: 12.5, color: 'var(--cyan)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{server.endpoint}</code>
            <button onClick={copia} title="Copia" style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiato ? 'var(--green)' : 'var(--text-dim)', padding: 2, display: 'flex', flexShrink: 0 }}>
              {copiato ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>
        <Stat label="Auth" value={server.auth_configurata ? '✓' : '—'} sub={server.auth_configurata ? 'bearer token' : 'non configurata'} color={server.auth_configurata ? 'var(--green)' : 'var(--red)'} />
        <Stat label="Tool" value={server.n_tools} sub="esposti" />
      </div>

      {/* Attività (live) */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <Stat label="Chiamate 24h" value={attivita.totale_24h} />
        <Stat label="Successo" value={attivita.successo_pct == null ? '—' : `${attivita.successo_pct}%`} color={attivita.successo_pct != null && attivita.successo_pct < 90 ? 'var(--yellow)' : 'var(--green)'} />
        <Stat label="Durata media" value={attivita.durata_media_ms == null ? '—' : `${attivita.durata_media_ms}ms`} />
        {attivita.top && attivita.top.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', flex: '1 1 240px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Top tool (24h)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {attivita.top.map(t => (
                <span key={t.tool} style={{ fontSize: 11.5, padding: '2px 8px', borderRadius: 6, background: 'var(--bg3)', color: 'var(--text-muted)' }}>
                  <code style={{ color: 'var(--cyan)' }}>{t.tool}</code> · {t.n}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Chiamate recenti */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 26 }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Chiamate recenti</div>
        <div style={{ overflowX: 'auto' }}>
          {(attivita.recenti || []).length === 0 ? (
            <div style={{ padding: 20, color: 'var(--text-dim)', fontSize: 13 }}>Nessuna chiamata registrata.</div>
          ) : (attivita.recenti || []).map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '22px 1fr 90px 70px 120px', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: i < attivita.recenti.length - 1 ? '1px solid var(--border)' : 'none', minWidth: 520 }}>
              {r.esito === 'ok' ? <CheckCircle2 size={15} color="var(--green)" /> : <XCircle size={15} color="var(--red)" />}
              <code style={{ fontSize: 12.5, color: 'var(--text)' }}>{r.tool}</code>
              <span style={{ fontSize: 11.5, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.agente}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{r.durata_ms}ms</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-dim)', textAlign: 'right' }}>{quando(r.quando)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Catalogo tool per area */}
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Tool esposti ({tools.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {AREA_ORDER.filter(a => areas[a]).map(area => {
          const Icon = AREA_ICON[area] || Boxes;
          return (
            <div key={area}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Icon size={16} color="var(--cyan)" />
                <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{area}</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>· {areas[area].length}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {areas[area].map(t => {
                  const on = aperto === t.name;
                  return (
                    <div key={t.name} onClick={() => setAperto(on ? null : t.name)}
                      style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '11px 13px', cursor: 'pointer' }}>
                      <code style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cyan)' }}>{t.name}</code>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4, ...(on ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
                        {t.description}
                      </div>
                      {on && t.params.length > 0 && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {t.params.map(p => (
                            <div key={p.name} style={{ fontSize: 11.5 }}>
                              <code style={{ color: 'var(--text)' }}>{p.name}</code>
                              <span style={{ color: 'var(--text-dim)' }}> : {p.type}{p.required ? ' *' : ''}</span>
                              {p.description && <span style={{ color: 'var(--text-muted)' }}> — {p.description}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
